import { VidstackPlayer, PlyrLayout } from 'vidstack/global/player'
import { NORWEGIAN } from '../../config/VIDEO_TRANSLATIONS.js'
// import { Dom } from '@brandocms/jupiter'

export default class VideoBlock {
  constructor(app, el) {
    this.app = app
    this.el = el
    this.initialize()
  }

  initialize() {
    this.overlay = this.el.querySelector('[data-video-overlay]')
    this.playButton = this.el.querySelector('[data-video-play]')
    this.playerContainer = this.el.querySelector('[data-video-player]')
    this.videoElement = this.playerContainer?.querySelector('video')
    this.isHLS = this.playerContainer?.querySelector('[data-hls]') !== null
    this.showControls = this.videoElement?.getAttribute('data-show-controls') === 'true'
    this.playing = false

    if (!this.videoElement) {
      console.warn('VideoBlock: No video element found')
      return
    }

    // Set up play button click handler
    if (this.playButton) {
      this.playButton.addEventListener('click', () => this.handlePlayClick())
    }

    // Initialize video player based on type
    if (this.isHLS) {
      this.initializeHLSPlayer()
    } else {
      this.initializeNativePlayer()
    }
  }

  handlePlayClick() {
    // Hide overlay with animation
    this.overlay.setAttribute('data-hidden', '')
    this.overlay.setAttribute('inert', '')
    this.playerContainer.setAttribute('data-visible', '')
    this.playerContainer.removeAttribute('inert')
    this.el.setAttribute('data-playing', 'true')

    // Start video playback
    if (this.player) {
      this.player.paused = false
    } else if (this.videoElement) {
      this.videoElement.play()
    }

    this.playing = true
  }

  showOverlay() {
    // Show overlay again for replay
    this.overlay.removeAttribute('data-hidden')
    this.overlay.removeAttribute('inert')
    this.playerContainer.removeAttribute('data-visible')
    this.playerContainer.setAttribute('inert', '')
    this.el.setAttribute('data-playing', 'false')
    this.playing = false

    // Reset video to start
    if (this.player) {
      this.player.currentTime = 0
      this.player.paused = true
    } else if (this.videoElement) {
      this.videoElement.currentTime = 0
      this.videoElement.pause()
    }
  }

  initializeHLSPlayer() {
    const hlsUrl = this.videoElement.getAttribute('data-src')
    const title = this.videoElement.getAttribute('data-title') || ''

    if (!hlsUrl) {
      console.warn('VideoBlock: No HLS URL found')
      return
    }

    // Validate that it's not an iframe URL
    if (hlsUrl.includes('iframe.mediadelivery.net')) {
      console.error('VideoBlock: Invalid URL - iframe embed URLs are not supported. Please use the HLS playlist URL (.m3u8) instead.')
      console.error('Expected format: https://[pull-zone-url]/[video-id]/playlist.m3u8')
      return
    }

    // Configure player options
    const playerOptions = {
      target: this.videoElement,
      title: title,
      src: {
        src: hlsUrl,
        type: hlsUrl.endsWith('.m3u8') ? 'application/x-mpegurl' : 'video/mp4',
      },
      load: 'eager',
      hideControlsOnMouseLeave: this.showControls,
    }

    // Add layout with controls if showControls is true
    if (this.showControls) {
      playerOptions.layout = new PlyrLayout({
        controls: [
          'play',
          'progress',
          'current-time',
          'mute',
          'volume',
          'settings',
          'fullscreen',
        ],
        translations: NORWEGIAN,
      })
    }

    // Create Vidstack player for HLS
    VidstackPlayer.create(playerOptions)
      .then(player => {
        this.player = player

        if (this.showControls) {
          this.player.controls.hideOnMouseLeave = true
          this.player.controls.hideDelay = 1000
        }

        // Listen for ended event to show overlay again
        this.player.addEventListener('ended', () => {
          this.showOverlay()
        })

        // Update playing state
        this.player.addEventListener('play', () => {
          this.el.setAttribute('data-playing', 'true')
          this.playing = true
        })

        this.player.addEventListener('pause', () => {
          if (this.player.state.ended) return // Don't update if video ended
          this.el.setAttribute('data-playing', 'false')
          this.playing = false
        })
      })
      .catch(error => {
        console.error('VideoBlock: Failed to initialize HLS player:', error)
      })
  }

  initializeNativePlayer() {
    // For SERVD or local videos
    const videoUrl = this.videoElement.getAttribute('src')
    const title = this.videoElement.getAttribute('data-title') || ''

    if (!videoUrl) {
      console.warn('VideoBlock: No video URL found')
      return
    }

    // Configure player options
    const playerOptions = {
      target: this.videoElement,
      title: title,
      src: {
        src: videoUrl,
        type: 'video/mp4',
      },
      load: 'eager',
      hideControlsOnMouseLeave: this.showControls,
    }

    // Add layout with controls if showControls is true
    if (this.showControls) {
      playerOptions.layout = new PlyrLayout({
        controls: [
          'play',
          'progress',
          'current-time',
          'mute',
          'volume',
          'settings',
          'fullscreen',
        ],
        translations: NORWEGIAN,
      })
    }

    // Create Vidstack player
    VidstackPlayer.create(playerOptions)
      .then(player => {
        this.player = player

        if (this.showControls) {
          this.player.controls.hideOnMouseLeave = true
          this.player.controls.hideDelay = 1000
        }

        // Listen for ended event to show overlay again
        this.player.addEventListener('ended', () => {
          this.showOverlay()
        })

        // Update playing state
        this.player.addEventListener('play', () => {
          this.el.setAttribute('data-playing', 'true')
          this.playing = true
        })

        this.player.addEventListener('pause', () => {
          if (this.player.state.ended) return // Don't update if video ended
          this.el.setAttribute('data-playing', 'false')
          this.playing = false
        })
      })
      .catch(error => {
        console.error('VideoBlock: Failed to initialize native player:', error)
      })
  }
}
